import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../contexts/AuthContext";

/**
 * Is the signed-in user staff? One GET /api/v1/staff/me per user per minute,
 * shared by the staff route guard and the brand menu's "Staff console" item.
 *
 * Keyed on the uid, so signing in as someone else never reuses an answer.
 * Never runs signed out. It runs when a component using it mounts: the
 * guard on /app/staff, and the brand menu's staff item, which mounts only
 * with the opened menu — so an ordinary visitor's page views never call it.
 *
 * Resolves to `{ staff: true, uid, email, granted_at, claimed }` or
 * `{ staff: false }`; rejects only on a network error or a 5xx.
 *
 * The API module is imported when the query first runs, not statically:
 * this hook sits in the main bundle (the gate, the brand menu), and a static
 * import would pull axios and the API client into it for every visitor.
 */
const fetchStaffMe = () => import("../api/staffApi").then((m) => m.fetchStaffMe());

export const staffMeKey = (uid) => ["staff-me", uid ?? null];

export const useStaffMe = ({ enabled = true } = {}) => {
  const { status, user } = useAuth();
  return useQuery({
    queryKey: staffMeKey(user?.uid),
    queryFn: fetchStaffMe,
    enabled: enabled && status === "signed-in" && !!user,
    staleTime: 60 * 1000,
  });
};
